import {
  useMutation,
  useQuery,
  useQueryClient,
  UseQueryOptions,
  UseQueryResult
} from 'react-query';
import { APIErrorType } from '../../api';
import {
  fetchStudentExam,
  fetchStudentExams,
  fetchStudentExamsChartData,
  finishStudentExam,
  startStudentExam,
  StudentExamChartType,
  StudentExamType
} from '../../api/student-exam';

export type useStudentExamsReturnType = UseQueryResult<StudentExamType[]>;

export function useStudentExams(): useStudentExamsReturnType {
  return useQuery<StudentExamType[], APIErrorType>('student-exams', fetchStudentExams, {
    staleTime: 30 * 1000,
    retry: false
  });
}

export const getStudentExamQueryKey: (examId: number) => [string, { examId: number }] = examId => [
  'student-exam',
  { examId }
];

export type useStudentExamChartReturnType = UseQueryResult<StudentExamChartType[]>;

export function useStudentExamsChartData(): useStudentExamChartReturnType {
  return useQuery<StudentExamChartType[], APIErrorType>(
    'student-exams-chart-data',
    fetchStudentExamsChartData,
    {
      staleTime: 30 * 1000,
      retry: false
    }
  );
}

export type useStudentExamReturnType = UseQueryResult<StudentExamType>;

export function useStudentExam(
  examId: number | undefined,
  options: Omit<
    UseQueryOptions<
      StudentExamType,
      APIErrorType,
      StudentExamType,
      ReturnType<typeof getStudentExamQueryKey>
    >,
    'queryKey | queryFn'
  > = {}
): useStudentExamReturnType {
  if (!examId) throw Error('studentExamId is undefiend');
  return useQuery<
    StudentExamType,
    APIErrorType,
    StudentExamType,
    ReturnType<typeof getStudentExamQueryKey>
  >(getStudentExamQueryKey(examId), fetchStudentExam, {
    staleTime: 30 * 1000,
    retry: false,
    ...options
  });
}

export function useStartStudentExamMutation() {
  const queryClient = useQueryClient();

  return useMutation<StudentExamType, APIErrorType, number>(startStudentExam, {
    onSuccess: async studentExam => {
      await queryClient.setQueryData(getStudentExamQueryKey(studentExam.exam.id), studentExam);
    }
  });
}

export function useFinishStudentExamMutation() {
  const queryClient = useQueryClient();

  return useMutation<StudentExamType, APIErrorType, number>(finishStudentExam, {
    onSuccess: async studentExam => {
      await queryClient.invalidateQueries('exams');
      await queryClient.invalidateQueries('student-exams');
      await queryClient.setQueryData(getStudentExamQueryKey(studentExam.exam.id), studentExam);
      await queryClient.invalidateQueries(['student-questions', { examId: studentExam.exam.id }]);
    }
  });
}
